import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../api";
import { toasts } from "../components/AppToast";

import {
  FiCheckCircle,
  FiLock,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";

export default function ChangePassword() {
  const [sideOpen, setSideOpen] = useState(true);
  const [activePage, setActivePage] = useState("Change Password");
  const [user, setUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [form, setForm] = useState({
    user_id: "",
    user_name: "",
    user_emailId: "",
    old_password: "",
    new_password: "",
    confirm_password: "",
  });

  // Get logged-in user and email
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      return;
    }

    const loadUser = async () => {
      try {
        const loggedUser = JSON.parse(
          localStorage.getItem("user") || "null"
        );

        if (!loggedUser?.user_id) {
          toasts.error("User information not found");
          return;
        }

        setUser(loggedUser);

        const response = await API.get(
          `/users/${loggedUser.user_id}`
        );

        const userData = response.data;

        setForm((prev) => ({
          ...prev,
          user_id:
            userData.user_id ||
            loggedUser.user_id ||
            "",
          user_name:
            userData.user_name ||
            loggedUser.user_name ||
            "",
          user_emailId:
            userData.user_emailId ||
            "",
        }));
      } catch (error) {
        console.error(
          "Fetch user details error:",
          error.response?.data || error.message
        );

        toasts.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Unable to load user information"
        );
      }
    };

    loadUser();
  }, []);

  // Handle input
  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Submit change password
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.user_id) {
      toasts.warning("User information not found");
      return;
    }

    if (!form.user_emailId) {
      toasts.warning("Email ID not found");
      return;
    }

    if (!form.old_password) {
      toasts.warning("Please enter old password");
      return;
    }

    if (!form.new_password) {
      toasts.warning("Please enter new password");
      return;
    }

    if (!form.confirm_password) {
      toasts.warning("Please confirm new password");
      return;
    }

    if (form.new_password !== form.confirm_password) {
      toasts.warning(
        "New password and confirm password do not match"
      );
      return;
    }

    if (form.old_password === form.new_password) {
      toasts.warning(
        "New password must be different from old password"
      );
      return;
    }

    try {
      setSaving(true);

      const response = await API.post(
        "/users/change-password",
        {
          user_id: form.user_id,
          user_emailId: form.user_emailId,
          old_password: form.old_password,
          new_password: form.new_password,
        }
      );

      if (response.data?.success !== false) {
        toasts.success(
          response.data?.message ||
            "Password changed successfully"
        );

        setForm((prev) => ({
          ...prev,
          old_password: "",
          new_password: "",
          confirm_password: "",
        }));

        setShowOldPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
      } else {
        toasts.error(
          response.data?.message ||
            "Failed to change password"
        );
      }
    } catch (error) {
      console.error(
        "Change password error:",
        error.response?.data || error.message
      );

      toasts.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to change password"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout
      sideOpen={sideOpen}
      setSideOpen={setSideOpen}
      activePage={activePage}
      setActivePage={setActivePage}
      user={user}
    >
      <main
        className="dashboard-main"
        style={{ overflowY: "auto" }}
      >
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-xl-8 col-lg-10 col-md-12">
              <div className="card shadow-sm border-0">

                {/* Header */}
                <div className="card-header bg-white py-3 px-4 d-flex align-items-center gap-3">
                  <div
                    className="form-card-icon rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: "45px",
                      height: "45px",
                      color: "#2C908D",
                      background:
                        "rgba(44, 144, 141, 0.1)",
                      flexShrink: 0,
                    }}
                  >
                    <FiLock
                      size={22}
                      color="#2C908D"
                    />
                  </div>

                  <div>
                    <h4
                      className="mb-0 fw-bold"
                      style={{ color: "#2C908D" }}
                    >
                      Change Password
                    </h4>

                    <p className="text-muted small mb-0">
                      Update your account security credentials
                    </p>
                  </div>
                </div>

                {/* Form */}
                <div className="card-body p-4">
                  <form
                    onSubmit={handleSubmit}
                    autoComplete="off"
                  >

                    {/* User Information */}
                    <div className="row g-3 mb-3">

                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          User Name
                        </label>

                        <input
                          type="text"
                          className="um-input1 bg-light custom-hover-input"
                          value={form.user_name}
                          readOnly
                          placeholder="User name"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Email ID
                        </label>

                        <input
                          type="email"
                          className="um-input1 bg-light custom-hover-input"
                          value={form.user_emailId}
                          readOnly
                          placeholder="Email address"
                        />
                      </div>

                    </div>

                    <hr className="my-3" />

                    {/* Old Password */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Old Password{" "}
                        <span className="text-danger">
                          *
                        </span>
                      </label>

                      <div
                        className="input-group"
                        style={{
                          flexWrap: "nowrap",
                        }}
                      >
                        <input
                          type={
                            showOldPassword
                              ? "text"
                              : "password"
                          }
                          className="um-input1 custom-hover-input"
                          style={{
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                          }}
                          value={form.old_password}
                          onChange={(e) =>
                            handleChange(
                              "old_password",
                              e.target.value
                            )
                          }
                          placeholder="Enter old password"
                          disabled={saving}
                        />

                        <button
                          type="button"
                          className="btn btn-outline-secondary px-3 custom-eye-btn"
                          onClick={() =>
                            setShowOldPassword(
                              (prev) => !prev
                            )
                          }
                          tabIndex={-1}
                        >
                          {showOldPassword ? (
                            <FiEyeOff size={18} />
                          ) : (
                            <FiEye size={18} />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* New and Confirm Password */}
                    <div className="row g-3">

                      {/* New Password */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            New Password{" "}
                            <span className="text-danger">
                              *
                            </span>
                          </label>

                          <div
                            className="input-group"
                            style={{
                              flexWrap: "nowrap",
                            }}
                          >
                            <input
                              type={
                                showNewPassword
                                  ? "text"
                                  : "password"
                              }
                              className="um-input1 custom-hover-input"
                              style={{
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0,
                              }}
                              value={form.new_password}
                              onChange={(e) =>
                                handleChange(
                                  "new_password",
                                  e.target.value
                                )
                              }
                              placeholder="Enter new password"
                              disabled={saving}
                            />

                            <button
                              type="button"
                              className="btn btn-outline-secondary px-3 custom-eye-btn"
                              onClick={() =>
                                setShowNewPassword(
                                  (prev) => !prev
                                )
                              }
                              tabIndex={-1}
                            >
                              {showNewPassword ? (
                                <FiEyeOff size={18} />
                              ) : (
                                <FiEye size={18} />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Confirm Password */}
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Confirm New Password{" "}
                            <span className="text-danger">
                              *
                            </span>
                          </label>

                          <div
                            className="input-group"
                            style={{
                              flexWrap: "nowrap",
                            }}
                          >
                            <input
                              type={
                                showConfirmPassword
                                  ? "text"
                                  : "password"
                              }
                              className="um-input1 custom-hover-input"
                              style={{
                                borderTopRightRadius: 0,
                                borderBottomRightRadius: 0,
                              }}
                              value={
                                form.confirm_password
                              }
                              onChange={(e) =>
                                handleChange(
                                  "confirm_password",
                                  e.target.value
                                )
                              }
                              placeholder="Confirm new password"
                              disabled={saving}
                            />

                            <button
                              type="button"
                              className="btn btn-outline-secondary px-3 custom-eye-btn"
                              onClick={() =>
                                setShowConfirmPassword(
                                  (prev) => !prev
                                )
                              }
                              tabIndex={-1}
                            >
                              {showConfirmPassword ? (
                                <FiEyeOff size={18} />
                              ) : (
                                <FiEye size={18} />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                    </div>

                    {/* Submit */}
                    <div className="d-flex justify-content-center mt-4">
                      <button
                        type="submit"
                        className="btn text-white px-5 py-2"
                        disabled={saving}
                        style={{
                          backgroundColor: "#2C908D",
                          borderColor: "#2C908D",
                          fontWeight: "600",
                          boxShadow: "none",
                          outline: "none",
                        }}
                      >
                        {saving ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            />
                            Saving...
                          </>
                        ) : (
                          <>
                            <FiCheckCircle className="me-2" />
                            Change Password
                          </>
                        )}
                      </button>
                    </div>

                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

     
    </Layout>
  );
}
